import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import AlertMessage from '../../components/ui/AlertMessage';
import AddIcon from '../../components/icons/AddIcon';
import ArrowLeftIcon from '../../components/icons/ArrowLeftIcon';
import PaginationArrowLeftIcon from '../../components/icons/PaginationArrowLeftIcon';
import PaginationArrowRightIcon from '../../components/icons/PaginationArrowRightIcon';


const ServiceListSupplierScreen = () => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const { token } = useAuth();
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [serviceImages, setServiceImages] = useState({}); // { [serviceId]: imageUrl }
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6; // Show 6 services per page

    useEffect(() => {
        const fetchServices = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await fetch(`${API_BASE_URL}/api/services/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: `Error fetching services: ${response.statusText}` }));
                    throw new Error(errorData.message || `Error fetching services: ${response.statusText}`);
                }
                const data = await response.json();
                setServices(data.reverse());

                // For each service, fetch its images and select the first available image from any input section
                const fetchAllImages = async () => {
                    const imagesMap = {};
                    await Promise.all(
                        data.map(async (service) => {
                            try {
                                const res = await fetch(`${API_BASE_URL}/api/service-images/service/${service.id}`, {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (!res.ok) return;
                                let images = await res.json();
                                images = images.filter(img => !img.deleted_at);
                                // Organize by input section
                                const categoryMap = {
                                    '1': ['habitacionPhotos', 'alojamientoPhotos', 'exterioresPhotos'],
                                    '2': ['platoPhotos', 'localPhotos', 'ambientePhotos'],
                                    '3': ['vehiculoInteriorPhotos', 'vehiculoExteriorPhotos'],
                                    '4': ['actividadPhotos', 'entornoPhotos'],
                                    '5': ['guiaProfilePhotos', 'guiaServicioPhotos'],
                                    '6': ['otrosServicioPhotos']
                                };
                                const catId = String(service.category_id);
                                const inputSections = categoryMap[catId] || [];
                                let found = null;
                                for (const section of inputSections) {
                                    const sectionImgs = images.filter(img => img.input_type === section);
                                    if (sectionImgs.length > 0) {
                                        found = sectionImgs[0];
                                        break;
                                    }
                                }
                                if (!found && images.length > 0) found = images[0];
                                if (found) imagesMap[service.id] = found.image_url;
                            } catch (e) {
                                // ignore
                            }
                        })
                    );
                    setServiceImages(imagesMap);
                };
                fetchAllImages();
            } catch (err) {
                setError(err.message);
                console.error("Error fetching services:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchServices();
    }, [API_BASE_URL, token]);

    return (
        <>
            <FormSpinnerOverlay isLoading={isLoading} />
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-5">
                    <div className="d-flex align-items-center">
                        <Link to="/home" className="btn btn-back-circle me-3" title="Volver a servicios">
                            <ArrowLeftIcon />
                        </Link>
                        <h2 className="mb-0">Mis servicios</h2>
                    </div>
                </div>
                <div className="btn-toolbar mb-2 mb-md-0">
                    <Link to="/services/add" className="btn btn-success d-flex align-items-center">
                        <AddIcon />
                        Añadir servicio
                    </Link>
                </div>
            </div>

            {error && <AlertMessage type="danger" message={`Error al cargar servicios: ${error}`} />}

            {!isLoading && !error && services.length === 0 && (
                <AlertMessage type="info" message="Aún no tienes servicios registrados. ¡Añade tu primer servicio!" />
            )}

            <div className="row row-cols-1 row-cols-lg-3 row-cols-md-2 gx-4 gy-md-5 gy-4 mb-5 justify-content-start">
                {services
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map(service => (
                        <div key={service.id}>
                            <div className="card card-service border-0">
                                <img
                                    src={
                                        serviceImages[service.id] ||
                                        service.image_url ||
                                        service.imageUrl ||
                                        "https://placehold.co/600x400/EFEFEF/AAAAAA&text=Servicio"
                                    }
                                    className="card-img-top"
                                    alt={service.name}
                                />
                                <div className="card-body p-3">
                                    <span className={`badge ms-auto ${service.availability_status_id === 1 || service.status === 'Disponible' ? 'bg-success' : 'bg-light text-dark'}`}>{service.availability_status_id === 1 ? 'Disponible' : 'No disponible'}</span>
                                    <h5 className="card-title mt-2">{service.name}</h5>
                                    <div className="d-flex align-items-baseline">
                                        <h4 className="me-2 mb-3">${service.price}</h4><span className="text-muted small">{service.price_description}</span>
                                    </div>
                                    <Link to={`/services/detail/${service.id}`} className="btn btn-checkin py-1 px-2">Ver más</Link>
                                </div>
                            </div>
                        </div>
                    ))}
            </div>

            {/* Functional Pagination */}
            {!isLoading && services.length > itemsPerPage && (
                <div className="row">
                    <div className="col-12 mt-4">
                        <nav aria-label="Page navigation">
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        aria-label="Anterior"
                                    >
                                        <PaginationArrowLeftIcon />
                                    </button>
                                </li>
                                {Array.from({ length: Math.ceil(services.length / itemsPerPage) }).map((_, index) => (
                                    <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(index + 1)}
                                        >
                                            {index + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === Math.ceil(services.length / itemsPerPage) ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(prev => Math.min(Math.ceil(services.length / itemsPerPage), prev + 1))}
                                        aria-label="Siguiente"
                                    >
                                        <PaginationArrowRightIcon />
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            )}
        </>
    );
};

export default ServiceListSupplierScreen;